import { Grid } from '@mui/material';
import { SimpleTagInput } from 'features/projectTags/components/TagInputs/SimpleTagInput';
import { BatteryMode, PowerSource } from 'features/projectTags/components/tags.types';
import { BATTERY_MODES, POWER_SOURCES } from 'features/projectTags/components/tags.utils';
import { useBreakpoint } from 'hooks/useBreakpoint';
import { useTranslation } from 'react-i18next';

export const baseTagNameFilter = {
  filterKey: 'tag_name__in',
};

const createTagNameFilter = (value: string | string[]) => ({
  ...baseTagNameFilter,
  value: Array.isArray(value) && value.length > 0 ? value.join(',') : '', //create comma seperated string expected by the backend
});

export const ProjectTagsFilter = ({ getFilterValues, updateFilters, gridSize }) => {
  const { t } = useTranslation();
  const [tagFilterValues] = getFilterValues(baseTagNameFilter.filterKey);
  const { isMobile } = useBreakpoint();

  //transform back into string array
  const tagFilterValuesArray =
    typeof tagFilterValues === 'string'
      ? tagFilterValues
          .split(',')
          .map((word) => word.trim())
          .filter(Boolean)
      : tagFilterValues;

  // Split the tag filter values into power sources and modes
  const powerSourceFilterValues =
    tagFilterValuesArray?.filter((value: PowerSource) => POWER_SOURCES.includes(value)) ?? [];

  const batteryModeFilterValues =
    tagFilterValuesArray?.filter((value: BatteryMode) => BATTERY_MODES.includes(value)) ?? [];

  const updateTagFilter = (tags: string[], input: 'powerSupply' | 'batteryMode') => {
    const relevantTags = input === 'powerSupply' ? POWER_SOURCES : BATTERY_MODES;

    // Combine existing and new tags while ensuring only relevant tags are updated
    const updatedTags = [
      ...(tagFilterValuesArray?.filter((tag: string) => !relevantTags.includes(tag) || tags.includes(tag)) ?? []),
      ...tags,
    ];

    const newTags = Array.from(new Set(updatedTags)); // Ensure no duplicates

    // Update the filters
    const newTagFilter = createTagNameFilter(newTags);
    updateFilters([newTagFilter]);
  };

  return (
    <>
      <Grid item {...gridSize} sx={{ minWidth: isMobile ? null : '180px' }}>
        <SimpleTagInput
          value={batteryModeFilterValues}
          label={t('projects:overview.filterSection.batteryMode')}
          placeholder={t('projects:overview.filterSection.selectMode')}
          inputType="batteryMode"
          onChange={(tags) => {
            updateTagFilter(tags, 'batteryMode');
          }}
          onDelete={(option) =>
            updateTagFilter(
              batteryModeFilterValues.filter((val: string) => val !== option),
              'batteryMode'
            )
          }
          fullWidth
          shrink
        />
      </Grid>
      <Grid item {...gridSize} sx={{ minWidth: isMobile ? null : '180px' }}>
        <SimpleTagInput
          value={powerSourceFilterValues}
          label={t('projects:overview.filterSection.powerSupply')}
          placeholder={t('projects:overview.filterSection.selectSupply')}
          inputType="powerSource"
          onChange={(tags) => updateTagFilter(tags, 'powerSupply')}
          onDelete={(option) =>
            updateTagFilter(
              powerSourceFilterValues.filter((val: string) => val !== option),
              'powerSupply'
            )
          }
          fullWidth
          shrink
        />
      </Grid>
    </>
  );
};
